import type { FieldOwnerState } from "@mui/x-date-pickers-pro";
import {
  DateTimeRangePicker,
  type DateTimeRangePickerProps,
} from "@mui/x-date-pickers-pro/DateTimeRangePicker";
import { MultiInputDateTimeRangeField } from "@mui/x-date-pickers-pro/MultiInputDateTimeRangeField";
import {
  type Control,
  type FieldPath,
  type FieldValues,
  type RegisterOptions,
  useController,
} from "react-hook-form";
import { useTranslation } from "react-i18next";

export interface ReactHookDateTimeRangePickerProps<
  TFieldValues extends FieldValues,
  TNameFrom extends FieldPath<TFieldValues>,
  TNameTo extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends DateTimeRangePickerProps<any> {
  fromName: TNameFrom;
  toName: TNameTo;
  fromLabel: string;
  toLabel: string;
  required?: boolean;
  rules?: Omit<
    RegisterOptions<TFieldValues, TNameFrom | TNameTo>,
    "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
  >;
  control?: Control<TFieldValues>;
  onChangeCallback?: (from: Date | null, to: Date | null) => void;
}

export function ReactHookDateTimeRangePicker<
  TFieldValues extends FieldValues,
  TNameFrom extends FieldPath<TFieldValues>,
  TNameTo extends FieldPath<TFieldValues>,
>({
  fromName,
  toName,
  fromLabel,
  toLabel,
  control,
  required,
  rules,
  onChangeCallback,
  ...dateRangePickerProps
}: ReactHookDateTimeRangePickerProps<TFieldValues, TNameFrom, TNameTo>) {
  const { t } = useTranslation("Fixhub");

  function isValidDate(value: unknown) {
    const isInstanceOfDate = value instanceof Date;
    if (!isInstanceOfDate) {
      return t("Next:Core:FieldRequiredValidationMessage");
    }
    const notInvalidDate = !Number.isNaN(value.getTime());
    return notInvalidDate || t("Next:Core:FieldRequiredValidationMessage");
  }

  const validate = required ? { isValidDate } : undefined;

  // startDate
  const {
    field: { value: fromValue, onChange: fromOnChange },
    fieldState: { error: fromError },
  } = useController({
    name: fromName,
    control,
    rules: {
      required: required && t("Next:Core:FieldRequiredValidationMessage"),
      validate,
      ...rules,
    },
  });

  // endDate
  const {
    field: { value: toValue, onChange: toOnChange },
    fieldState: { error: toError },
  } = useController({
    name: toName,
    control,
    rules: {
      required: required && t("Next:Core:FieldRequiredValidationMessage"),
      validate,
      ...rules,
    },
  });

  return (
    <DateTimeRangePicker
      localeText={{
        start: required ? `* ${fromLabel}` : fromLabel,
        end: required ? `* ${toLabel}` : toLabel,
      }}
      value={[fromValue, toValue]}
      onChange={([newFrom, newTo]) => {
        fromOnChange(newFrom);
        toOnChange(newTo);
        if (typeof onChangeCallback === "function") {
          onChangeCallback(newFrom, newTo);
        }
      }}
      {...dateRangePickerProps}
      slots={{ field: MultiInputDateTimeRangeField }}
      slotProps={{
        textField: ({
          position,
        }: FieldOwnerState & { position?: "start" | "end" }) => ({
          // TODO figure out how to add endAdornment without breaking popover anchoring
          error: position === "start" ? Boolean(fromError) : Boolean(toError),
          helperText:
            position === "start" ? fromError?.message : toError?.message,
          ...dateRangePickerProps.slotProps?.textField,
        }),
      }}
    />
  );
}
